import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, TriangleAlert } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import {
    AddGenerationShot,
    ApproveGenerationCharacters,
    CancelVideoGeneration,
    DeleteGenerationShot,
    MoveGenerationShot,
    GenerateGenerationCharacters,
    GenerateGenerationShots,
    GetIdeationIdea,
    GetVideoGeneration,
    RebreakdownVideoGeneration,
    RegenerateGenerationCharacter,
    UseCastMemberForCharacter,
    RegenerateGenerationShot,
    SaveGenerationCharacterToLibrary,
    SelectGenerationTake,
    StartVideoGeneration,
    StitchVideoGeneration,
    UnapproveGenerationCharacters,
    UpdateGenerationCharacter,
    UpdateGenerationShot,
    UpdateVideoGeneration,
    UploadGenerationCharacterImage,
} from '../../Client/ManagementServiceClient';
import { CastLibrary } from './CastLibrary';
import { CastPicker } from './CastPicker';
import { useWallet } from '../../hooks/useWallet';
import './styles.css';

// The four stages the user is walked through. Everything in this component is
// organised around showing exactly one of them at a time: the prototype this
// grew out of showed all four at once, which made a long, expensive, mostly
// sequential process look like a wall of controls.
const STEPS = [
    { key: 'script', label: 'Script', hint: 'Write or paste what happens.' },
    { key: 'cast', label: 'Cast', hint: 'Lock in how each character looks.' },
    { key: 'shots', label: 'Shots', hint: 'Render the scenes, one take at a time.' },
    { key: 'final', label: 'Final cut', hint: 'Stitch the takes into one video.' },
];

const APPROVED_STAGES = ['characters_approved', 'shots_ready', 'complete'];

// Where the run's own stage says the user should be. Used to place them on
// arrival and to move them on when a phase completes — never to trap them, since
// any reachable step stays clickable.
const stepForStage = (generation) => {
    const stage = generation?.Stage || 'draft';
    if (stage === 'complete') return 'final';
    if (stage === 'shots_ready') return 'final';
    if (APPROVED_STAGES.includes(stage)) return 'shots';
    if (stage === 'characters_ready' || stage === 'breakdown_ready') return 'cast';
    return 'script';
};

const isRunning = (generation) => generation?.ActiveJob?.Status === 'running';

// Keep the url already on screen for any blob we have seen before, taking only
// the ones that are new. Identity is preserved when nothing was added, so a
// poll that changes no media re-renders nothing downstream either.
const keepKnownUrls = (previous, incoming) => {
    const blobs = Object.keys(incoming);
    let added = false;
    const merged = {};
    blobs.forEach((blob) => {
        const known = previous[blob];
        if (!known) added = true;
        merged[blob] = known || incoming[blob];
    });
    if (!added && blobs.length === Object.keys(previous).length) return previous;
    return merged;
};

const POLL_INTERVAL_MS = 3000;
// The server signs media urls for 60 minutes and mints fresh ones on every
// response, so the same clip comes back under a different url three seconds
// later. Handing that to a <video> resets it mid-playback, so known urls are
// kept and only swapped when the signatures are old enough to be worth it.
const MEDIA_URL_REFRESH_MS = 45 * 60 * 1000;
// How long to keep watching after an action, for the worker to pick the phase
// up. Its poll interval is 5s, so this is generous on purpose; the run's own
// "running" status takes over the moment it does.
const PICKUP_GRACE_MS = 60000;

// Speech budget, mirroring seconds_for_dialogue on the server so the editor can
// show the floor it is about to enforce. The video model speaks slowly, and a
// take that runs out mid-sentence is a render the user paid for and cannot use,
// so the length of a dialogue shot is decided by its words.
const SPEAKER_LABEL = /^\s*[A-Z0-9][A-Z0-9 .'-]{0,29}(\([^)]*\))?\s*:\s*/;

const spokenWords = (dialogue) => (dialogue || '')
    .split('\n')
    .reduce((total, line) => total + line.replace(SPEAKER_LABEL, '').replace(/\([^)]*\)/g, ' ').split(/\s+/).filter(Boolean).length, 0);

// Seconds these lines want, before the model's ceiling is applied — a value
// above the ceiling is the signal that no duration can hold them.
const rawSecondsForDialogue = (dialogue, limits) => {
    const words = spokenWords(dialogue);
    if (!words) return 0;
    return Math.ceil(words / (limits.speech_words_per_second || 2) + (limits.dialogue_lead_seconds ?? 2));
};

const secondsForDialogue = (dialogue, limits) => {
    const low = limits.min_shot_seconds || 3;
    const raw = rawSecondsForDialogue(dialogue, limits);
    if (!raw) return low;
    return Math.min(limits.max_shot_seconds || 10, Math.max(low, raw));
};

function StepBar({ current, reachable, onSelect }) {
    return (
        <ol className="gen-steps">
            {STEPS.map((step, index) => {
                const state = step.key === current
                    ? 'current'
                    : reachable.includes(step.key) ? 'done' : 'locked';
                return (
                    <li key={step.key} className={`gen-step gen-step-${state}`}>
                        <button
                            type="button"
                            onClick={() => reachable.includes(step.key) && onSelect(step.key)}
                            disabled={state === 'locked'}
                            title={state === 'locked' ? 'Finish the earlier steps first' : step.hint}
                        >
                            <span className="gen-step-number">{index + 1}</span>
                            <span className="gen-step-label">{step.label}</span>
                        </button>
                    </li>
                );
            })}
        </ol>
    );
}

// Which call re-runs a phase that failed. A failed job does not block the
// queue, and every phase resumes rather than restarting, so retrying is just
// asking for the same phase again.
const RETRY_BY_KIND = {
    breakdown: RebreakdownVideoGeneration,
    characters: GenerateGenerationCharacters,
    shots: GenerateGenerationShots,
    shot: GenerateGenerationShots,
    stitch: StitchVideoGeneration,
};

// How each retry is priced. A retry resumes rather than restarts -- the worker
// only touches what is not Ready -- so the quote is for the remaining work, not
// the whole phase. Stitching is local ffmpeg work and reserves nothing unless it
// has to generate a music bed, so it asks rather than assuming free.
const RETRY_ESTIMATE_BY_KIND = {
    breakdown: { phase: 'breakdown' },
    characters: { phase: 'characters' },
    shots: { phase: 'shots', from_index: 0, force: false },
    shot: { phase: 'shots', from_index: 0, force: false },
    stitch: { phase: 'stitch' },
};

function JobBanner({ generation, onCancel, onRetry }) {
    const job = generation?.ActiveJob;
    if (!job) return null;

    if (job.Status === 'running') {
        const showCount = job.Total > 1;
        const percent = showCount ? Math.round((job.Current / job.Total) * 100) : null;
        return (
            <div className="gen-banner gen-banner-working">
                <div className="gen-banner-text">
                    <strong>Working…</strong> {job.Message}
                    {showCount ? ` (${job.Current} of ${job.Total})` : ''}
                </div>
                {percent !== null && (
                    <div className="gen-progress"><div className="gen-progress-bar" style={{ width: `${percent}%` }} /></div>
                )}
                <button type="button" className="gen-btn gen-btn-ghost" onClick={onCancel}>Stop</button>
            </div>
        );
    }
    if (job.Status === 'failed') {
        const canRetry = Boolean(RETRY_BY_KIND[job.Kind]);
        return (
            <div className="gen-banner gen-banner-bad">
                <strong>{job.Kind} didn’t finish.</strong> {job.Error || job.Message}
                <span className="gen-banner-note">
                    Anything already finished is kept, so running it again picks up where it stopped.
                </span>
                {canRetry && (
                    <button type="button" className="gen-btn gen-btn-primary" onClick={onRetry}>
                        Try {job.Kind} again
                    </button>
                )}
            </div>
        );
    }
    if (job.Status === 'cancelled') {
        return <div className="gen-banner gen-banner-note-only">Stopped. Everything rendered so far is kept.</div>;
    }
    return null;
}

function GenerationStudio({ workspaceId, workspaceName, ideaId = '', onClose, onAssetsChanged }) {
    const [generation, setGeneration] = useState(null);
    const [mediaUrls, setMediaUrls] = useState({});
    const [limits, setLimits] = useState({});
    const [step, setStep] = useState('script');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showLibrary, setShowLibrary] = useState(false);
    const { confirmCost } = useWallet();

    // Script-step draft, kept local so typing is not fought by the poller.
    const [draft, setDraft] = useState({ Title: '', Script: '', Style: '', AspectRatio: '16:9' });
    const [draftLoaded, setDraftLoaded] = useState(false);
    const [seededFromIdea, setSeededFromIdea] = useState(false);

    const stepTouched = useRef(false);
    // Latest run, readable from the polling tick without restarting the timer.
    const generationRef = useRef(null);
    // Until when to keep polling even though the run does not say "running":
    // queuing a phase and the worker picking it up are seconds apart, and in
    // between the document still looks idle.
    const awaitingUntil = useRef(0);
    // Assets change when shots land; the parent refreshes its list on this edge
    // rather than polling storage itself.
    const lastShotSignature = useRef('');
    // When the urls currently on screen were signed, so they can be taken again
    // before they expire without churning on every poll.
    const mediaSignedAt = useRef(0);

    const token = useCallback(async () => getAuth().currentUser.getIdToken(), []);

    const applyResponse = useCallback((payload) => {
        if (!payload?.Generation) return;
        generationRef.current = payload.Generation;
        // The worker has the run: stop covering for the queue-to-pickup gap and
        // let the job's own status drive the polling from here.
        if (isRunning(payload.Generation)) awaitingUntil.current = 0;
        setGeneration(payload.Generation);
        const stale = Date.now() - mediaSignedAt.current > MEDIA_URL_REFRESH_MS;
        if (stale) mediaSignedAt.current = Date.now();
        setMediaUrls((previous) => (
            stale ? (payload.MediaUrls || {}) : keepKnownUrls(previous, payload.MediaUrls || {})
        ));
        if (payload.Limits) setLimits(payload.Limits);
    }, []);

    const load = useCallback(async () => {
        try {
            const idToken = await token();
            const { status, myJson } = await GetVideoGeneration(idToken, workspaceId);
            if (status === 404) {
                generationRef.current = null;
                setGeneration(null);
                return;
            }
            if (status !== 200) throw new Error(myJson?.detail || 'Could not load this run');
            applyResponse(myJson);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [applyResponse, token, workspaceId]);

    useEffect(() => { load(); }, [load]);

    // One timer for the life of the studio, deciding on each tick whether there
    // is anything to ask about.
    //
    // Scheduling the next poll from the run's own state — the obvious shape —
    // has two holes this avoids. A phase that is queued but not yet picked up
    // does not say "running", so nothing would start the polling and the studio
    // would sit on a stale snapshot until it was reopened. And a poll that
    // failed changed no state, so nothing re-ran the effect that scheduled the
    // next one: a single hiccup left the run reading "Working…" forever.
    useEffect(() => {
        const timer = setInterval(() => {
            if (isRunning(generationRef.current) || Date.now() < awaitingUntil.current) {
                load();
            }
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [load]);

    // Follow the run's stage until the user navigates by hand; after that, stay
    // where they put themselves.
    useEffect(() => {
        if (!generation || stepTouched.current) return;
        setStep(stepForStage(generation));
    }, [generation]);

    useEffect(() => {
        if (!generation || draftLoaded) return;
        setDraft({
            Title: generation.Title || '',
            Script: generation.Script || '',
            Style: generation.Style || '',
            AspectRatio: generation.AspectRatio || '16:9',
        });
        setDraftLoaded(true);
    }, [generation, draftLoaded]);

    // A workspace reached from an idea already has the script the user wrote,
    // so carry it in instead of asking them to paste it again. Only for a run
    // that has not started — once the server holds a script, that one wins.
    useEffect(() => {
        if (loading || generation || draftLoaded || seededFromIdea || !ideaId) return;
        let cancelled = false;
        (async () => {
            try {
                const idToken = await token();
                const { status, myJson } = await GetIdeationIdea(idToken, ideaId);
                if (cancelled || status !== 200) return;
                const text = String(myJson?.text || '').trim();
                if (!text) return;
                setDraft((prev) => (prev.Script.trim() ? prev : {
                    ...prev,
                    // Seeded from the project's name -- the only name there is.
                    Title: prev.Title || workspaceName || '',
                    Script: text,
                }));
                setSeededFromIdea(true);
            } catch {
                // Prefill is a convenience; the field still works by hand.
            }
        })();
        return () => { cancelled = true; };
    }, [loading, generation, draftLoaded, seededFromIdea, ideaId, workspaceName, token]);

    useEffect(() => {
        if (!generation || !onAssetsChanged) return;
        const signature = (generation.Shots || []).map((shot) => `${shot.ShotId}:${shot.SelectedTakeId}`).join('|');
        if (signature && signature !== lastShotSignature.current) {
            lastShotSignature.current = signature;
            onAssetsChanged();
        }
    }, [generation, onAssetsChanged]);

    const selectStep = (key) => { stepTouched.current = true; setStep(key); };

    // One wrapper for every mutating call: clears the last message, surfaces the
    // failure if there is one, and refreshes from the server rather than trying
    // to patch local state in parallel with the poller.
    //
    // `confirmPhase` is the query the server prices this click at
    // ({ phase, from_index | shot_index | character_name }). Passing it means the
    // user sees what the click costs and can say no — which matters most here,
    // where a single button can be a dozen video renders. The server decides
    // whether the number is big enough to actually interrupt for.
    const run = useCallback(async (action, successNote = '', confirmPhase = null) => {
        if (confirmPhase) {
            const proceed = await confirmCost({
                flow: 'video_generation',
                generation: { workspaceId, params: confirmPhase },
            });
            if (!proceed) return null;
        }
        setBusy(true);
        setError('');
        setNotice('');
        try {
            const idToken = await token();
            const { status, myJson } = await action(idToken);
            if (status >= 400) throw new Error(myJson?.detail || 'That did not work');
            // Watch for a while after any action: the ones that queue a phase
            // return before the worker has touched the run, so its status is
            // not yet evidence of anything.
            awaitingUntil.current = Date.now() + PICKUP_GRACE_MS;
            if (myJson?.Generation) applyResponse(myJson);
            else await load();
            if (successNote) setNotice(successNote);
            return myJson;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setBusy(false);
        }
    }, [applyResponse, confirmCost, load, token, workspaceId]);

    const reachable = useMemo(() => {
        const steps = ['script'];
        if (!generation) return steps;
        if ((generation.Shots || []).length || (generation.Characters || []).length) steps.push('cast');
        if (APPROVED_STAGES.includes(generation.Stage)) steps.push('shots');
        if ((generation.Shots || []).length && generation.Shots.every((shot) => (shot.Takes || []).length)) {
            steps.push('final');
        }
        return steps;
    }, [generation]);

    const locked = busy || isRunning(generation);

    if (loading) return <div className="gen-studio gen-loading">Loading…</div>;

    return (
        <div className="gen-studio">
            <div className="gen-header">
                <div>
                    <h3>Generate the video</h3>
                    <p className="gen-subtitle">
                        {workspaceName ? `${workspaceName} — ` : ''}
                        script in, finished cut out. Every shot lands as an asset you can keep editing.
                    </p>
                </div>
                <div className="gen-header-actions">
                    <button type="button" className="gen-btn gen-btn-ghost" onClick={() => setShowLibrary((open) => !open)}>
                        {showLibrary ? 'Hide cast library' : 'Cast library'}
                    </button>
                    <button type="button" className="gen-btn gen-btn-ghost" onClick={onClose}>
                        <ArrowLeft size={14} aria-hidden="true" /> Back to the editor
                    </button>
                </div>
            </div>

            <StepBar current={step} reachable={reachable} onSelect={selectStep} />
            <p className="gen-step-hint">{STEPS.find((entry) => entry.key === step)?.hint}</p>

            {error && <div className="gen-banner gen-banner-bad">{error}</div>}
            {notice && <div className="gen-banner gen-banner-ok">{notice}</div>}
            <JobBanner
                generation={generation}
                onCancel={() => run((idToken) => CancelVideoGeneration(idToken, workspaceId), 'Stopping after the current step.')}
                onRetry={() => {
                    const kind = generation?.ActiveJob?.Kind;
                    const retry = RETRY_BY_KIND[kind];
                    if (!retry) return;
                    run(
                        (idToken) => retry(idToken, workspaceId),
                        'Running it again…',
                        RETRY_ESTIMATE_BY_KIND[kind],
                    );
                }}
            />

            {showLibrary && <CastLibrary onError={setError} onNotice={setNotice} />}

            {step === 'script' && (
                <ScriptStep
                    generation={generation}
                    draft={draft}
                    setDraft={setDraft}
                    locked={locked}
                    limits={limits}
                    seededFromIdea={seededFromIdea && !generation}
                    onStart={() => run(
                        (idToken) => StartVideoGeneration(idToken, { VideoWorkspaceId: workspaceId, ...draft }),
                        'Reading your script and planning the shots…',
                        // No run exists yet, so there is nothing stored to price
                        // the breakdown from; it is a single cheap LLM call, and
                        // the reservation still refuses it if unaffordable.
                    )}
                    onSave={() => run(
                        (idToken) => UpdateVideoGeneration(idToken, workspaceId, draft),
                        'Saved.',
                    )}
                    onRebreak={() => run(
                        (idToken) => RebreakdownVideoGeneration(idToken, workspaceId),
                        'Re-planning the shots from your script…',
                        { phase: 'breakdown' },
                    )}
                    onNext={() => selectStep('cast')}
                />
            )}

            {step === 'cast' && generation && (
                <CastStep
                    generation={generation}
                    mediaUrls={mediaUrls}
                    locked={locked}
                    workspaceId={workspaceId}
                    run={run}
                    onError={setError}
                    onNext={() => selectStep('shots')}
                    reload={load}
                />
            )}

            {step === 'shots' && generation && (
                <ShotsStep
                    generation={generation}
                    mediaUrls={mediaUrls}
                    locked={locked}
                    limits={limits}
                    workspaceId={workspaceId}
                    run={run}
                    onNext={() => selectStep('final')}
                />
            )}

            {step === 'final' && generation && (
                <FinalStep
                    generation={generation}
                    mediaUrls={mediaUrls}
                    locked={locked}
                    run={run}
                    onStitch={() => run(
                        (idToken) => StitchVideoGeneration(idToken, workspaceId),
                        'Stitching the final cut…',
                    )}
                    onClose={onClose}
                />
            )}
        </div>
    );
}

function ScriptStep({ generation, draft, setDraft, locked, limits, seededFromIdea, onStart, onSave, onRebreak, onNext }) {
    const started = Boolean(generation);
    const shotCount = (generation?.Shots || []).length;
    const set = (field) => (event) => setDraft({ ...draft, [field]: event.target.value });

    return (
        <div className="gen-panel">
            <div className="gen-field">
                <label htmlFor="gen-title">Title</label>
                <input id="gen-title" value={draft.Title} onChange={set('Title')} placeholder="Untitled" disabled={locked} />
            </div>

            <div className="gen-field">
                <label htmlFor="gen-script">Script</label>
                <textarea
                    id="gen-script"
                    className="gen-script"
                    value={draft.Script}
                    onChange={set('Script')}
                    disabled={locked}
                    placeholder="Paste or write your script. Action, dialogue, scene changes — plain prose is fine."
                />
                <small>
                    {seededFromIdea && 'Filled in from your idea — edit it here and the idea stays as it is. '}
                    It gets split into shots of {limits.min_shot_seconds || 3}–{limits.max_shot_seconds || 10} seconds,
                    each one continuing from the last frame of the one before it.
                </small>
            </div>

            <div className="gen-field-row">
                <div className="gen-field">
                    <label htmlFor="gen-style">Visual style</label>
                    <input id="gen-style" value={draft.Style} onChange={set('Style')} disabled={locked} />
                </div>
                <div className="gen-field gen-field-narrow">
                    <label htmlFor="gen-aspect">Shape</label>
                    <select id="gen-aspect" value={draft.AspectRatio} onChange={set('AspectRatio')} disabled={locked}>
                        <option value="16:9">Landscape (16:9)</option>
                        <option value="9:16">Vertical (9:16)</option>
                    </select>
                </div>
            </div>

            <div className="gen-actions">
                {!started ? (
                    <button type="button" className="gen-btn gen-btn-primary" onClick={onStart} disabled={locked || !draft.Script.trim()}>
                        Plan the shots
                    </button>
                ) : (
                    <>
                        <button type="button" className="gen-btn" onClick={onSave} disabled={locked}>Save changes</button>
                        <button type="button" className="gen-btn gen-btn-ghost" onClick={onRebreak} disabled={locked}>
                            Re-plan from this script
                        </button>
                        {shotCount > 0 && (
                            <button type="button" className="gen-btn gen-btn-primary" onClick={onNext} disabled={locked}>
                                Next: the cast <ArrowRight size={14} aria-hidden="true" />
                            </button>
                        )}
                    </>
                )}
            </div>

            {shotCount > 0 && (
                <p className="gen-summary">
                    Planned {shotCount} shot{shotCount === 1 ? '' : 's'} and{' '}
                    {(generation.Characters || []).length} character{(generation.Characters || []).length === 1 ? '' : 's'}.
                    Re-planning replaces the shot list, so do it before you start rendering.
                </p>
            )}
        </div>
    );
}

function CastStep({ generation, mediaUrls, locked, workspaceId, run, onNext, reload, onError }) {
    const characters = generation.Characters || [];
    const approved = APPROVED_STAGES.includes(generation.Stage);
    const allReady = characters.length > 0 && characters.every((character) => character.Status === 'ready');
    const anyDrawn = characters.some((character) => character.ImageBlobName);

    return (
        <div className="gen-panel">
            <p className="gen-explainer">
                These portraits are what keeps a face the same from shot to shot. Nothing is rendered
                until you approve them — that is the one gate in this flow, and it is there because
                re-rendering after the fact costs real money.
            </p>

            <div className="gen-actions">
                {!approved ? (
                    <>
                        <button
                            type="button"
                            className="gen-btn"
                            onClick={() => run(
                                (idToken) => GenerateGenerationCharacters(idToken, workspaceId),
                                'Drawing the cast…',
                                { phase: 'characters' },
                            )}
                            disabled={locked || characters.length === 0}
                        >
                            {anyDrawn ? 'Draw the missing portraits' : 'Draw all portraits'}
                        </button>
                        <button
                            type="button"
                            className="gen-btn gen-btn-primary"
                            onClick={async () => {
                                const result = await run(
                                    (idToken) => ApproveGenerationCharacters(idToken, workspaceId),
                                    'Cast approved — you can render shots now.',
                                );
                                if (result) onNext();
                            }}
                            disabled={locked || !allReady}
                            title={allReady ? 'Approve and move on' : 'Every character needs a portrait first'}
                        >
                            Approve the cast <ArrowRight size={14} aria-hidden="true" />
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className="gen-btn gen-btn-ghost"
                            onClick={() => run((idToken) => UnapproveGenerationCharacters(idToken, workspaceId), 'Reopened for edits.')}
                            disabled={locked}
                        >
                            Reopen for edits
                        </button>
                        <button type="button" className="gen-btn gen-btn-primary" onClick={onNext} disabled={locked}>
                            Next: the shots <ArrowRight size={14} aria-hidden="true" />
                        </button>
                    </>
                )}
            </div>

            {characters.length === 0 && <p className="gen-empty">This script has no named characters — you can skip straight to the shots.</p>}

            <div className="gen-cast-grid">
                {characters.map((character) => (
                    <CharacterCard
                        key={character.Name}
                        character={character}
                        imageUrl={mediaUrls[character.ImageBlobName]}
                        locked={locked}
                        workspaceId={workspaceId}
                        run={run}
                        reload={reload}
                        onError={onError}
                    />
                ))}
            </div>
        </div>
    );
}

function CharacterCard({ character, imageUrl, locked, workspaceId, run, reload, onError }) {
    const [prompt, setPrompt] = useState(character.AppearancePrompt || '');
    const [voice, setVoice] = useState(character.VoicePrompt || '');
    const [open, setOpen] = useState(false);
    const [picking, setPicking] = useState(false);
    const fileRef = useRef(null);

    const upload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await run(
            (idToken) => UploadGenerationCharacterImage(idToken, workspaceId, character.Name, file),
            `Using your image for ${character.Name}.`,
        );
        if (fileRef.current) fileRef.current.value = '';
        reload();
    };

    return (
        <div className={`gen-cast-card gen-cast-${character.Status}${character.Approved ? ' gen-cast-approved' : ''}`}>
            <div className="gen-cast-head">
                <strong>{character.Name}</strong>
                <span className={`gen-pill gen-pill-${character.Status}`}>
                    {character.Approved ? 'approved' : character.Status}
                </span>
            </div>

            {imageUrl ? (
                <img src={imageUrl} alt={character.Name} className="gen-portrait" />
            ) : (
                <div className="gen-portrait gen-portrait-empty">
                    {character.Status === 'generating' ? 'drawing…' : 'no portrait yet'}
                </div>
            )}

            <p className="gen-cast-description">{character.Description}</p>
            {character.Error && <p className="gen-error-text">{character.Error}</p>}

            <button type="button" className="gen-link" onClick={() => setOpen((value) => !value)}>
                {open ? 'Hide character notes' : 'Edit how they look and sound'}
            </button>
            {open && (
                <>
                    <div className="gen-field">
                        <label>Appearance</label>
                        <textarea
                            value={prompt}
                            onChange={(event) => setPrompt(event.target.value)}
                            disabled={locked}
                            rows={4}
                        />
                    </div>
                    <div className="gen-field">
                        <label>Voice</label>
                        <textarea
                            value={voice}
                            onChange={(event) => setVoice(event.target.value)}
                            disabled={locked}
                            rows={2}
                            placeholder="low, gravelly male voice, late 50s, slow Texan drawl"
                        />
                        {/* The video model casts a voice from the prompt on every
                            take, so this is sent again with each shot. Editing it
                            only affects shots rendered from here on. */}
                        <small>Sent with every shot they speak in, so they keep one voice.</small>
                    </div>
                    <button
                        type="button"
                        className="gen-btn gen-btn-sm"
                        disabled={locked}
                        onClick={() => run(
                            (idToken) => UpdateGenerationCharacter(idToken, workspaceId, character.Name, {
                                AppearancePrompt: prompt,
                                VoicePrompt: voice,
                            }),
                            'Saved. Redraw to see it.',
                        )}
                    >
                        Save notes
                    </button>
                </>
            )}

            <div className="gen-cast-actions">
                <button
                    type="button"
                    className="gen-btn gen-btn-sm"
                    disabled={locked}
                    onClick={() => run(
                        (idToken) => RegenerateGenerationCharacter(idToken, workspaceId, character.Name),
                        `Redrawing ${character.Name}…`,
                        { phase: 'characters', character_name: character.Name },
                    )}
                >
                    Redraw
                </button>
                <button type="button" className="gen-btn gen-btn-sm gen-btn-ghost" disabled={locked} onClick={() => fileRef.current?.click()}>
                    Use my own
                </button>
                {/* The other half of "Save to library" below. A portrait cannot be
                    reproduced -- the same prompt never draws the same face twice --
                    so a character who recurs has to be picked rather than redrawn. */}
                <button
                    type="button"
                    className="gen-btn gen-btn-sm gen-btn-ghost"
                    disabled={locked}
                    onClick={() => setPicking(true)}
                >
                    Cast from library
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={upload} hidden />
                {character.ImageBlobName && (
                    <button
                        type="button"
                        className="gen-btn gen-btn-sm gen-btn-ghost"
                        disabled={locked}
                        onClick={() => run(
                            (idToken) => SaveGenerationCharacterToLibrary(idToken, workspaceId, character.Name),
                            `${character.Name} saved to your cast library.`,
                        )}
                    >
                        Save to library
                    </button>
                )}
            </div>

            {picking && (
                <CastPicker
                    characterName={character.Name}
                    onError={onError}
                    onClose={() => setPicking(false)}
                    onPick={async (member) => {
                        setPicking(false);
                        await run(
                            (idToken) => UseCastMemberForCharacter(idToken, workspaceId, character.Name, member.CastId),
                            `${character.Name} is now ${member.Name}.`,
                        );
                        // The portrait url is signed and per-character, so the
                        // card cannot show the new face until the run reloads.
                        reload();
                    }}
                />
            )}
        </div>
    );
}

function ShotsStep({ generation, mediaUrls, locked, limits, workspaceId, run, onNext }) {
    const ordered = [...(generation.Shots || [])].sort((a, b) => a.Index - b.Index);
    const rendered = ordered.filter((shot) => (shot.Takes || []).length).length;
    const allRendered = ordered.length > 0 && rendered === ordered.length;

    return (
        <div className="gen-panel">
            <div className="gen-shots-summary">
                <div>
                    <strong>{rendered} of {ordered.length} shots rendered.</strong>
                    <span className="gen-muted"> Each one picks up from the frame the shot before it ends on in the final cut, unless it is marked a hard cut.</span>
                </div>
                <div className="gen-actions">
                    {/* Two separate buttons rather than one that changes meaning: a
                        part-finished run is the case where "generate" is ambiguous,
                        and the expensive reading of it must not be the default. */}
                    <button
                        type="button"
                        className="gen-btn gen-btn-primary"
                        disabled={locked || ordered.length === 0 || allRendered}
                        onClick={() => run(
                            (idToken) => GenerateGenerationShots(idToken, workspaceId, 0, false),
                            'Rendering the shots…',
                            { phase: 'shots', from_index: 0, force: false },
                        )}
                    >
                        {rendered ? 'Render the missing shots' : 'Render all shots'}
                    </button>
                    {rendered > 0 && (
                        <button
                            type="button"
                            className="gen-btn gen-btn-ghost"
                            disabled={locked}
                            onClick={() => {
                                if (!window.confirm(`Re-render all ${ordered.length} shots from scratch? The ${rendered} already rendered are replaced and charged again.`)) return;
                                run(
                                    (idToken) => GenerateGenerationShots(idToken, workspaceId, 0, true),
                                    'Rendering every shot again…',
                                    { phase: 'shots', from_index: 0, force: true },
                                );
                            }}
                        >
                            Render everything again
                        </button>
                    )}
                    {allRendered && (
                        <button type="button" className="gen-btn gen-btn-primary" onClick={onNext} disabled={locked}>
                            Next: the final cut <ArrowRight size={14} aria-hidden="true" />
                        </button>
                    )}
                </div>
            </div>

            {ordered.map((shot) => (
                <ShotCard
                    key={shot.ShotId}
                    shot={shot}
                    total={ordered.length}
                    mediaUrls={mediaUrls}
                    locked={locked}
                    limits={limits}
                    workspaceId={workspaceId}
                    run={run}
                />
            ))}

            <button
                type="button"
                className="gen-btn gen-btn-ghost gen-btn-wide"
                disabled={locked}
                onClick={() => run(
                    (idToken) => AddGenerationShot(idToken, workspaceId, { AtIndex: null, Scene: 'New scene', HardCut: true }),
                    'Scene added — describe it, then render it.',
                )}
            >
                + Add a scene at the end
            </button>
        </div>
    );
}

function ShotCard({ shot, total, mediaUrls, locked, limits, workspaceId, run }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        Scene: shot.Scene || '',
        Prompt: shot.Prompt || '',
        Dialogue: shot.Dialogue || '',
        ContinuityNote: shot.ContinuityNote || '',
        DurationSeconds: shot.DurationSeconds || 8,
        HardCut: Boolean(shot.HardCut),
    });

    const takes = shot.Takes || [];
    const selected = takes.find((take) => take.TakeId === shot.SelectedTakeId) || takes[takes.length - 1];
    const set = (field, value) => setForm({ ...form, [field]: value });

    // What these lines need, and whether they fit at all. A block over the
    // ceiling cannot be saved by a longer take — it has to be split, which the
    // hint says outright rather than leaving the user to re-render and guess.
    const maximum = limits.max_shot_seconds || 10;
    const needed = secondsForDialogue(form.Dialogue, limits);
    const overflows = rawSecondsForDialogue(form.Dialogue, limits) > maximum;

    const saveThen = async (alsoRegenerate) => {
        await run((idToken) => UpdateGenerationShot(idToken, workspaceId, shot.Index, form), alsoRegenerate ? '' : 'Saved.');
        if (!alsoRegenerate) return;
        const result = await run(
            (idToken) => RegenerateGenerationShot(idToken, workspaceId, shot.Index),
            `Re-rendering shot ${shot.Index + 1}…`,
            // Priced after the save above, so a changed duration is quoted at
            // its new length rather than the one on screen a moment ago.
            { phase: 'shots', shot_index: shot.Index },
        );
        if (result?.StaleDownstream?.length) {
            // Surfaced rather than acted on: cascading would silently spend money.
            const next = result.StaleDownstream[0] + 1;
            window.setTimeout(() => window.alert(
                `Shot ${next} still continues from the old frame. Re-render it too once this finishes if you want the join to stay seamless.`,
            ), 0);
        }
    };

    return (
        <div className={`gen-shot gen-shot-${shot.Status}`}>
            <div className="gen-shot-head">
                <strong>{shot.Index + 1}. {shot.Scene || 'Untitled scene'}</strong>
                <div className="gen-shot-tags">
                    <span className="gen-pill">{shot.DurationSeconds}s</span>
                    <span className="gen-pill">{shot.HardCut ? 'hard cut' : 'continues'}</span>
                    <span className={`gen-pill gen-pill-${shot.Status}`}>{shot.Status}</span>
                </div>
            </div>

            <div className="gen-shot-body">
                <div className="gen-shot-media">
                    {selected && mediaUrls[selected.BlobName] ? (
                        <video
                            src={mediaUrls[selected.BlobName]}
                            poster={mediaUrls[selected.ThumbnailBlobName] || undefined}
                            controls
                            preload="metadata"
                        />
                    ) : (
                        <div className="gen-shot-placeholder">
                            {shot.Status === 'generating' ? 'rendering…' : 'not rendered yet'}
                        </div>
                    )}

                    {takes.length > 1 && (
                        <div className="gen-takes">
                            {takes.map((take, position) => (
                                <button
                                    key={take.TakeId}
                                    type="button"
                                    className={`gen-btn gen-btn-sm ${take.TakeId === shot.SelectedTakeId ? 'gen-btn-primary' : 'gen-btn-ghost'}`}
                                    disabled={locked}
                                    onClick={() => run(
                                        (idToken) => SelectGenerationTake(idToken, workspaceId, shot.Index, take.TakeId),
                                        `Using take ${position + 1} for this shot.`,
                                    )}
                                >
                                    take {position + 1}
                                    {take.DialogueIssues?.length ? <> <TriangleAlert size={12} aria-hidden="true" /><span className="sr-only"> has dialogue issues</span></> : null}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* What the take actually said. Shown only when it differs
                        from the line, because a take that spoke it correctly has
                        nothing to report — and shown with the transcript, since
                        "it repeated a word" is not something the user can check
                        without scrubbing the clip themselves. */}
                    {selected?.DialogueIssues?.length > 0 && (
                        <div className="gen-take-check">
                            <span className="gen-warn-text">
                                Dialogue check: {selected.DialogueIssues.join('; ')}
                                {typeof selected.DialogueMatch === 'number'
                                    && ` (${Math.round(selected.DialogueMatch * 100)}% of the line matched)`}
                            </span>
                            {selected.Transcript && (
                                <p className="gen-take-heard">Heard: “{selected.Transcript}”</p>
                            )}
                            {/* The take stays as it was rendered; the cut is
                                where the stumbles come out. Worth saying plainly,
                                because the clip above still has them in it and
                                otherwise reads as the version that ships. */}
                            {selected.SpeechCuts?.length > 0 && (
                                <p className="gen-muted">
                                    {selected.SpeechCuts.length === 1 ? '1 stumble' : `${selected.SpeechCuts.length} stumbles`}
                                    {' '}will be cut out of the final video
                                    {selected.SpeechCuts.some((cut) => cut.Text)
                                        && `: ${selected.SpeechCuts.filter((cut) => cut.Text).map((cut) => `“${cut.Text}”`).join(', ')}`}
                                    .
                                </p>
                            )}
                            <p className="gen-muted">
                                It was re-rendered automatically where that was worth another take.
                                Refine the line below and render again if this one is not usable.
                            </p>
                        </div>
                    )}
                </div>

                <div className="gen-shot-detail">
                    <p className="gen-shot-action">{shot.Action || shot.Prompt}</p>
                    {shot.Dialogue && <p className="gen-shot-dialogue">{shot.Dialogue}</p>}
                    {shot.Error && <p className="gen-error-text">{shot.Error}</p>}

                    <button type="button" className="gen-link" onClick={() => setOpen((value) => !value)}>
                        {open ? 'Hide the details' : 'Refine this shot'}
                    </button>

                    {open && (
                        <div className="gen-shot-form">
                            <div className="gen-field">
                                <label>Scene name</label>
                                <input value={form.Scene} onChange={(event) => set('Scene', event.target.value)} disabled={locked} />
                            </div>
                            <div className="gen-field">
                                <label>What the camera sees</label>
                                <textarea value={form.Prompt} onChange={(event) => set('Prompt', event.target.value)} disabled={locked} rows={4} />
                            </div>
                            <div className="gen-field">
                                <label>Dialogue</label>
                                <textarea value={form.Dialogue} onChange={(event) => set('Dialogue', event.target.value)} disabled={locked} rows={2} />
                                {overflows ? (
                                    <small className="gen-warn-text">
                                        Too many words for one {maximum}s take — the line will be cut off at the end.
                                        Split it: shorten this shot's dialogue, then add a scene after it that carries
                                        the rest with “hard cut” off, so the speech continues.
                                    </small>
                                ) : spokenWords(form.Dialogue) > 0 && (
                                    <small>
                                        {spokenWords(form.Dialogue)} spoken words — needs about {needed}s to finish.
                                    </small>
                                )}
                            </div>
                            <div className="gen-field">
                                <label>How it picks up from the last shot</label>
                                <textarea
                                    value={form.ContinuityNote}
                                    onChange={(event) => set('ContinuityNote', event.target.value)}
                                    disabled={locked || form.HardCut}
                                    rows={2}
                                />
                            </div>
                            <div className="gen-field-row">
                                <div className="gen-field gen-field-narrow">
                                    <label>Seconds</label>
                                    <input
                                        type="number"
                                        min={needed}
                                        max={maximum}
                                        value={form.DurationSeconds}
                                        onChange={(event) => set('DurationSeconds', parseInt(event.target.value, 10) || 8)}
                                        disabled={locked}
                                    />
                                    {form.DurationSeconds < needed && (
                                        <small className="gen-warn-text">Saved as {needed}s so the dialogue finishes.</small>
                                    )}
                                </div>
                                <label className="gen-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={form.HardCut}
                                        onChange={(event) => set('HardCut', event.target.checked)}
                                        disabled={locked}
                                    />
                                    Hard cut — start fresh instead of continuing
                                </label>
                            </div>

                            <div className="gen-actions">
                                <button type="button" className="gen-btn gen-btn-primary" disabled={locked} onClick={() => saveThen(true)}>
                                    Save and re-render
                                </button>
                                <button type="button" className="gen-btn" disabled={locked} onClick={() => saveThen(false)}>
                                    Save only
                                </button>
                                <button
                                    type="button"
                                    className="gen-btn gen-btn-ghost"
                                    disabled={locked}
                                    onClick={() => run(
                                        // Forced: "from here on" is asked for by
                                        // someone looking at this shot who wants it
                                        // redone, not a resume.
                                        (idToken) => GenerateGenerationShots(idToken, workspaceId, shot.Index, true),
                                        `Re-rendering from shot ${shot.Index + 1} on…`,
                                        { phase: 'shots', from_index: shot.Index, force: true },
                                    )}
                                >
                                    Re-render from here on
                                </button>
                                {/* Moving a scene re-renders nothing -- takes are stored
                                    under the shot's own id -- so these are plain
                                    actions, not something to warn about. Disabled at
                                    the ends rather than hidden, so the row of controls
                                    does not change shape from card to card. */}
                                <button
                                    type="button"
                                    className="gen-btn gen-btn-ghost"
                                    disabled={locked || shot.Index === 0}
                                    title="Move this scene earlier"
                                    onClick={() => run(
                                        (idToken) => MoveGenerationShot(idToken, workspaceId, shot.Index, shot.Index - 1),
                                        'Scene moved earlier.',
                                    )}
                                >
                                    <ArrowUp size={14} aria-hidden="true" /> Move up
                                </button>
                                <button
                                    type="button"
                                    className="gen-btn gen-btn-ghost"
                                    disabled={locked || shot.Index >= total - 1}
                                    title="Move this scene later"
                                    onClick={() => run(
                                        (idToken) => MoveGenerationShot(idToken, workspaceId, shot.Index, shot.Index + 1),
                                        'Scene moved later.',
                                    )}
                                >
                                    <ArrowDown size={14} aria-hidden="true" /> Move down
                                </button>
                                <button
                                    type="button"
                                    className="gen-btn gen-btn-ghost gen-btn-danger"
                                    disabled={locked || total <= 1}
                                    onClick={() => {
                                        if (!window.confirm(`Delete scene "${shot.Scene}"? Its video is removed and the rest renumber.`)) return;
                                        run((idToken) => DeleteGenerationShot(idToken, workspaceId, shot.Index), 'Scene deleted.');
                                    }}
                                >
                                    Delete scene
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function MusicField({ generation, locked, run }) {
    const [enabled, setEnabled] = useState(!!generation.MusicEnabled);
    const [prompt, setPrompt] = useState(generation.MusicPrompt || '');

    const save = (nextEnabled, nextPrompt) => run(
        (idToken) => UpdateVideoGeneration(idToken, generation.VideoWorkspaceId, {
            MusicEnabled: nextEnabled,
            MusicPrompt: nextPrompt,
        }),
        'Saved.',
    );

    return (
        <div className="gen-field">
            <label className="gen-checkbox">
                <input
                    type="checkbox"
                    checked={enabled}
                    disabled={locked}
                    onChange={(event) => {
                        setEnabled(event.target.checked);
                        save(event.target.checked, prompt);
                    }}
                />
                Score the finished cut
            </label>
            {enabled && (
                <>
                    <textarea
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        onBlur={() => save(enabled, prompt)}
                        disabled={locked}
                        rows={2}
                        placeholder="slow synth dread, sparse, no drums"
                    />
                    {/* The bed is kept and reused, so only a changed brief costs
                        another song — worth saying, since stitching again is
                        otherwise free and users do it often. */}
                    <small>
                        One track over the whole video, ducked under the dialogue. Stitching again
                        reuses it; changing this generates a new one.
                    </small>
                </>
            )}
        </div>
    );
}

function FinalStep({ generation, mediaUrls, locked, run, onStitch, onClose }) {
    const finalUrl = mediaUrls[generation.FinalBlobName];
    const shots = generation.Shots || [];
    const ready = shots.length > 0 && shots.every((shot) => (shot.Takes || []).length);

    return (
        <div className="gen-panel">
            <MusicField generation={generation} locked={locked} run={run} />
            {/* A failed score never fails the stitch, so without this the user
                is looking at a finished video with silence where the music they
                asked for should be, and nothing anywhere saying why. */}
            {generation.MusicEnabled && generation.MusicError && (
                <p className="gen-error-text">
                    The score could not be generated, so this cut has no music: {generation.MusicError}
                </p>
            )}
            {finalUrl ? (
                <>
                    <video className="gen-final-video" src={finalUrl} controls />
                    <p className="gen-explainer">
                        This is now the workspace's current output version — the same thing an uploaded
                        cut would be. Every shot is also in your asset list, so you can trim it, add
                        effects, or hand it to the AI editor before posting.
                    </p>
                    <div className="gen-actions">
                        <a className="gen-btn gen-btn-ghost" href={finalUrl} download>Download</a>
                        <button type="button" className="gen-btn" onClick={onStitch} disabled={locked}>Stitch again</button>
                        <button type="button" className="gen-btn gen-btn-primary" onClick={onClose}>Open in the editor <ArrowRight size={14} aria-hidden="true" /></button>
                    </div>
                </>
            ) : (
                <>
                    <p className="gen-explainer">
                        Joining {shots.length} shots into one video. It is re-encoded so the takes line
                        up cleanly, which takes a minute or two.
                    </p>
                    <div className="gen-actions">
                        <button type="button" className="gen-btn gen-btn-primary" onClick={onStitch} disabled={locked || !ready}>
                            Stitch the final cut
                        </button>
                    </div>
                    {!ready && <p className="gen-muted">Every shot needs a take you are happy with first.</p>}
                </>
            )}
        </div>
    );
}

export { GenerationStudio };
